<script>
import examEnhancements from "@/mixins/examEnhancements";
import Breadcrumb from '@/components/global/breadcrumb.vue';
import axios from "axios";
import Swal from "sweetalert2";
import Dropdown from 'primevue/dropdown';
import { useGradeStore } from '@/stores/useGradeStore.js';
import MultiSelect from 'primevue/multiselect';
import Calendar from 'primevue/calendar';
import InputText from 'primevue/inputtext';
import InputSwitch from 'primevue/inputswitch';
import InputNumber from 'primevue/inputnumber';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import useVuelidate from "@vuelidate/core";
import {required} from "@vuelidate/validators";
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

export default {
  mixins: [examEnhancements],
  name: "add-exam",
  data() {
    return {
      v$: useVuelidate(),
      searchQuery: '',
      gradeSlug: this.$route.params.gradeSlug,
      title: "",
      date: null,
      selectedQuestions: [],
      questions: [],
      from_time: null,
      to_time: null,
      // duration: null,
      lesson: null,
      lessons: [],
      unit: null,
      units: [],
      branch: null,
      branches: [],
      duration_required: false,
      question_order: 'fixed', // 'fixed' = same order for all; 'random' = per-student
      questionOrderOptions: [
        { label: 'نفس الترتيب لكل الطلاب', value: 'fixed', icon: 'pi-sort-alt' },
        { label: 'ترتيب عشوائي لكل طالب', value: 'random', icon: 'pi-sort-alt-slash' },
      ],
      navigation_mode: 'free', // 'free' | 'sequential' | 'sequential_back'; default free
      navigationModeOptions: [
        { label: 'سؤال بسؤال بدون رجوع', value: 'sequential', icon: 'pi-arrow-left',
          desc: 'يجب الإجابة على كل سؤال بالترتيب ولا يمكن الرجوع للأسئلة السابقة' },
        { label: 'سؤال بسؤال مع إمكانية الرجوع', value: 'sequential_back', icon: 'pi-replay',
          desc: 'يجب الإجابة بالترتيب، ويمكن الرجوع للأسئلة التي تم الوصول إليها' },
        { label: 'تنقل حر', value: 'free', icon: 'pi-arrows-h',
          desc: 'يمكن للطالب التنقل بين جميع الأسئلة بحرية' },
      ],
      formSending: false,
    };
  },
  validations() {
    return {
      title: { required },
      date: { required },
      question_ids: this.examSettings.automated ? {} : { required },
      from_time: this.duration_required ? { required } : {},
      to_time: this.duration_required ? { required } : {},
      // Add more validations as needed
    };
  },
  components: {
    Breadcrumb,
    Dropdown,
    MultiSelect,
    Calendar,
    InputText,
    InputSwitch,
    InputNumber,
    Button,
    DatePicker,
    DataTable,
    Column
  },
  computed: {
    breadcrumbs() {
      return [
        { name: 'الرئيسية', path: '/' },
        { name: "الامتحانات", path: `/${this.gradeSlug}/exams` },
        { name: "إضافة امتحان", path: `/${this.gradeSlug}/exam/add` },
      ];
    },
    gradeStore() { return useGradeStore(); },
    resolvedGrade() {
      const slug = this.gradeSlug;
      return this.gradeStore.activeGrades.find(g => g.slug === slug)
          || this.gradeStore.allGrades.find(g => g.slug === slug)
          || null;
    },
    gradeId() { return this.resolvedGrade?.id ?? null; },
    customLabelBranch() {
      return option => option ? `${option.title} (${option.description})` : '';
    },
    customLabelUnit() {
      return option => option ? `${option.title} (${option.description})` : '';
    },
    customLabelLesson() {
      return option => option ? `${option.title} (${option.description})` : '';
    },
    duration() {
      if (!this.from_time || !this.to_time) return null;

      // تأكد إننا بنتعامل مع Date objects
      const getDateObject = (value) => {
        if (value instanceof Date) return value;
        return new Date(value); // لو string
      };

      let start = getDateObject(this.from_time);
      let end = getDateObject(this.to_time);

      // نضبط التاريخ عشان ما يأثرش (نشتغل بالوقت بس)
      const today = new Date();
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate(), start.getHours(), start.getMinutes());
      end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), end.getHours(), end.getMinutes());

      let diffMs = end - start;

      // لو الوقت النهائي أصغر → يعني اليوم التاني (مثل 11:30 pm → 1:30 am)
      if (diffMs < 0) {
        end.setDate(end.getDate() + 1);
        diffMs = end - start;
      }

      const diffMinutes = Math.floor(diffMs / 60000);

      // حماية من القيم الغريبة
      if (diffMinutes <= 0 || diffMinutes > 1440) return null;

      return diffMinutes;
    },
    question_ids: {
      get() {
        return this.selectedQuestions.map(q => q.id);
      },
      set(ids) {
        this.selectedQuestions = this.questions.filter(q => ids.includes(q.id));
      }
    },
    totalDegree() {
      return this.selectedQuestions.reduce((sum, q) => sum + (q.degree || 0), 0);
    },
    filteredQuestions() {
      const q = (this.searchQuery || '').toString().trim().toLowerCase();
      if (!q) return this.questions;
      return this.questions.filter(item => {
        const title = (item.title || '').toString().toLowerCase();
        const type = (item.type || '').toString().toLowerCase();
        const degree = (item.degree || '').toString().toLowerCase();
        const difficulty = (this.difficultyLabel(item.difficulty_level) || '').toString().toLowerCase();
        return title.includes(q) || type.includes(q) || degree.includes(q) || difficulty.includes(q);
      });
    }
  },
  methods: {
    fetchBranch() {
      axios.get(`/lessons?type=1&academic_year_id=${this.gradeId}`)
          .then(res => this.branches = res.data.data)
          .catch(() => this.branches = []);
    },
    fetchUnits() {
      if (!this.branch?.id) return;
      axios.get(`/lessons?type=2&academic_year_id=${this.gradeId}&parent_id=${this.branch.id}`)
        .then(res => {
          this.units = res.data.data;
          this.fetchQuestions();
        })
        .catch(() => this.units = []);
    },
    fetchLessons() {
      if (!this.unit?.id) return;
      axios.get(`/lessons?type=3&academic_year_id=${this.gradeId}&parent_id=${this.unit.id}`)
          .then(res => {
            this.lessons = res.data.data;
            this.fetchQuestions();
          })
          .catch(() => this.lessons = []);
    },
    fetchQuestions() {
      const params = this.isCourseScope ? { course_id: this.courseId } : { academic_year_id: this.gradeId };
      const scope = this.lesson || this.unit || this.chapter || this.branch;
      if (scope?.id) params.lesson_id = scope.id;
      if (this.lesson_ids?.length) {
        delete params.lesson_id;
        params.lesson_ids = this.lesson_ids.map(l => l.id || l);
      }
      axios.get('/questions', { params })
          .then(res => this.questions = res.data.data)
          .catch(() => this.questions = []);
    },
    formatDate(date) {
      if (!date) return "";

      let d;

      if (typeof date === 'string') {
        d = new Date(date);
      } else {
        d = date;
      }

      // لو مش تاريخ صحيح
      if (isNaN(d)) return "";

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },
    formatTimeForBackend(date, use24Hour = false) {
      if (!date) return '';

      const d = new Date(date);
      if (isNaN(d)) return '';

      const hours24 = d.getHours();
      const minutes = d.getMinutes().toString().padStart(2, '0');

      if (use24Hour) {
        // صيغة 24 ساعة → 22:30
        return `${hours24.toString().padStart(2, '0')}:${minutes}`;
      } else {
        // صيغة 12 ساعة → 10:30 pm
        let hours = hours24 % 12;
        hours = hours ? hours : 12; // الساعة 0 تصير 12
        const ampm = hours24 >= 12 ? 'pm' : 'am';
        return `${hours}:${minutes} ${ampm}`;
      }
    },
    difficultyLabel(value) {
      const map = { easy: 'سهل', medium: 'متوسط', hard: 'صعب' };
      return map[value] || value || '';
    },
    sendForm() {
      if (!this.validateEnhancements()) return;
      if (!this.date || (!this.examSettings.automated && this.question_ids.length === 0)) {
        Swal.fire({ icon: "warning", title: "يرجى ملء الحقول المطلوبة" });
        return;
      }
      this.formSending = true;

      const formData = new FormData();
      this.appendEnhancements(formData);
      formData.append('academic_year_id', this.gradeId);
      formData.append('title', this.title);
      formData.append('date', this.formatDate(this.date));
      formData.append('total_degree', this.totalDegree)
      formData.append('duration_required', this.duration_required ? 1 : 0);
      formData.append('question_order', this.question_order);
      formData.append('navigation_mode', this.navigation_mode);
      if(this.duration_required) {
        formData.append('from_time', this.formatTimeForBackend(this.from_time, true));
        formData.append('to_time', this.formatTimeForBackend(this.to_time, true));
        formData.append('duration', this.duration);
      }
      if(this.lesson) {
        formData.append('lesson_id', this.lesson?.id);
      }

      // For arrays, append each item individually
      if (!this.examSettings.automated) this.question_ids.forEach(id => formData.append('question_ids[]', id));

      axios.post('/exams', formData)
          .then(() => {
            Swal.fire({ icon: "success", title: "تم إضافة الامتحان بنجاح" });
            this.$router.push(`/${this.gradeSlug}/exams`);
          })
          .catch(error => {
            Swal.fire({ icon: "error", title: error.response?.data?.message || "حدث خطأ" });
          })
          .finally(() => {
            this.formSending = false;
          });
    }
  },
  async mounted() {
    if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
    if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
    this.fetchBranch();
    this.fetchQuestions();
  }
};
</script>

<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="form-page">
    <div class="form-page-header">
      <div class="form-page-header__icon">
        <i class="pi pi-file-export"></i>
      </div>
      <div class="form-page-header__text">
        <h1 class="form-page-header__title">إضافة امتحان</h1>
        <p class="form-page-header__subtitle">إنشاء امتحان جديد وتحديد أسئلته</p>
      </div>
    </div>

    <div class="container-fluid">
      <form @submit.prevent="sendForm">
        <!-- Location hierarchy -->
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-sitemap"></i></div>
            <span class="form-card__title">الموقع في المنهج</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">الفرع</label>
                  <Dropdown v-model="branch" :options="branches" showClear :optionLabel="customLabelBranch" placeholder="اختر الفرع" class="w-100" @update:model-value="resetLocation('branch')" />
                </div>
              </div>
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">الوحدة</label>
                  <Dropdown v-model="unit" :options="units" showClear :optionLabel="customLabelUnit" placeholder="اختر الوحدة" class="w-100" @update:model-value="resetLocation('parent')" />
                </div>
              </div>
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">الدروس</label>
                  <MultiSelect v-model="lesson_ids" :options="lessons" :optionLabel="customLabelLesson" placeholder="اختر الدروس" :maxSelectedLabels="2" class="w-100" @update:model-value="fetchQuestions" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Exam settings -->
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-cog"></i></div>
            <span class="form-card__title">إعدادات الامتحان</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">اسم الامتحان <span class="required">*</span></label>
                  <InputText id="title" v-model="title" placeholder="أدخل اسم الامتحان" class="w-100" />
                  <span class="field-error" v-if="v$.title.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال اسم الامتحان
                  </span>
                </div>
              </div>
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">تاريخ الامتحان <span class="required">*</span></label>
                  <Calendar v-model="date" dateFormat="yy-mm-dd" showIcon class="w-100" />
                  <span class="field-error" v-if="v$.date.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال التاريخ
                  </span>
                </div>
              </div>
            </div>

            <div class="source-toggle mt-4">
              <i class="pi pi-clock" style="color: var(--text-secondary);"></i>
              <span class="source-toggle__label">تحديد وقت الامتحان</span>
              <InputSwitch v-model="duration_required" />
              <span class="source-toggle__hint">{{ duration_required ? 'سيتم تحديد وقت بداية ونهاية' : 'بدون توقيت محدد' }}</span>
            </div>

            <div class="row g-4 mt-2" v-if="duration_required">
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">من الساعة <span class="required">*</span></label>
                  <DatePicker v-model="from_time" hourFormat="12" timeOnly fluid class="w-100" />
                  <span class="field-error" v-if="v$.from_time.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء اختيار وقت البدء
                  </span>
                </div>
              </div>
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">إلى الساعة <span class="required">*</span></label>
                  <DatePicker v-model="to_time" hourFormat="12" timeOnly fluid class="w-100" />
                  <span class="field-error" v-if="v$.to_time.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء اختيار وقت الانتهاء
                  </span>
                </div>
              </div>
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">المدة المحسوبة</label>
                  <div class="exam-duration-display">
                    <i class="pi pi-clock"></i>
                    <span>{{ duration ? `${duration} دقيقة` : '—' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Question order -->
            <div class="form-field mt-4">
              <label class="field-label">ترتيب الأسئلة</label>
              <div class="type-selector">
                <label v-for="opt in questionOrderOptions" :key="opt.value"
                  :class="['type-selector__item', { active: question_order === opt.value }]">
                  <input type="radio" v-model="question_order" :value="opt.value" class="d-none" />
                  <i :class="`pi ${opt.icon}`"></i>
                  {{ opt.label }}
                </label>
              </div>
            </div>

            <!-- Question navigation -->
            <div class="form-field mt-4">
              <label class="field-label">التنقل بين الأسئلة</label>
              <div class="type-selector">
                <label v-for="opt in navigationModeOptions" :key="opt.value"
                  :class="['type-selector__item', { active: navigation_mode === opt.value }]">
                  <input type="radio" v-model="navigation_mode" :value="opt.value" class="d-none" />
                  <i :class="`pi ${opt.icon}`" style="margin-inline-end:.4rem;"></i>
                  <span style="display:inline-flex;flex-direction:column;text-align:start;line-height:1.3;">
                    <span>{{ opt.label }}</span>
                    <small style="color:var(--text-muted);font-weight:400;">{{ opt.desc }}</small>
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <ExamEnhancementSettings v-model="examSettings" :timed="duration_required" />

        <!-- Question selection -->
        <div class="form-card" v-if="!examSettings.automated">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-list-check"></i></div>
            <span class="form-card__title">اختيار الأسئلة <span style="color:var(--color-danger);font-size:0.75rem;">*</span></span>
          </div>
          <div class="form-card__body">
            <div class="exam-search-bar mb-3">
              <i class="pi pi-search exam-search-bar__icon"></i>
              <InputText v-model="searchQuery" placeholder="بحث في الأسئلة بالعنوان أو النوع أو الدرجة..." class="w-100" />
            </div>
            <DataTable
              :value="filteredQuestions"
              selectionMode="multiple"
              :selection="selectedQuestions"
              @update:selection="val => selectedQuestions = val"
              dataKey="id"
              class="w-100"
            >
              <Column selectionMode="multiple" headerStyle="width: 3em"></Column>
              <Column field="title" header="عنوان السؤال"></Column>
              <Column field="type" header="النوع"></Column>
              <Column field="degree" header="الدرجة"></Column>
              <Column header="الصعوبة">
                <template #body="{ data }">{{ difficultyLabel(data.difficulty_level) }}</template>
              </Column>
            </DataTable>
            <span class="field-error mt-2" v-if="v$.question_ids.$error">
              <i class="pi pi-exclamation-circle"></i> برجاء اختيار الأسئلة
            </span>

            <div class="exam-summary-card mt-4" v-if="selectedQuestions.length > 0">
              <div class="exam-summary-card__item">
                <span class="exam-summary-card__label">عدد الأسئلة</span>
                <span class="exam-summary-card__value">{{ selectedQuestions.length }}</span>
              </div>
              <div class="exam-summary-card__divider"></div>
              <div class="exam-summary-card__item">
                <span class="exam-summary-card__label">مجموع الدرجات</span>
                <span class="exam-summary-card__value exam-summary-card__value--primary">{{ totalDegree }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn-submit" :disabled="formSending">
            <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
            <i class="pi pi-check" v-else></i>
            {{ formSending ? 'جاري الإرسال...' : 'حفظ الامتحان' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
